import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { relativeTime } from "@/lib/relativeTime";
import { toast } from "sonner";

const POLL_INTERVAL_MS = 20000;

/**
 * NotificationBell — bell in AppHeader with a portalled dropdown panel.
 *
 * The panel is portalled to <body> and positioned with an absolute
 * viewport-anchored top/right so it never gets clipped by the sticky
 * header's overflow. On narrow viewports (<420px) it snaps to full-width
 * with 16px side padding.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [pos, setPos] = useState(null); // {top, right, width}
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (e) {
      // Polling error — logged for diagnostics, no toast (bell polls every 20s).
      console.error("[NotificationBell] load failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  // Compute panel position anchored to the bell trigger.
  const recalcPos = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const isNarrow = vw < 480;
    // On narrow viewports the panel snaps to full-width minus 16px gutter on
    // each side. On wider viewports it's anchored to the bell at 380px wide.
    const desiredWidth = 380;
    const width = isNarrow ? Math.max(280, vw - 32) : desiredWidth;
    const right = isNarrow ? 16 : Math.max(16, vw - r.right);
    const top = r.bottom + 12;
    setPos({ top, right, width });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    recalcPos();
    window.addEventListener("resize", recalcPos);
    window.addEventListener("scroll", recalcPos, true);
    return () => {
      window.removeEventListener("resize", recalcPos);
      window.removeEventListener("scroll", recalcPos, true);
    };
  }, [open, recalcPos]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const openPanel = () => {
    setOpen((v) => {
      const next = !v;
      if (next) load();
      return next;
    });
  };

  const markOneRead = async (id) => {
    setItems((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
    );
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await api.post(`/notifications/${id}/read`);
    } catch (e) {
      console.error("[NotificationBell] mark-one-read failed:", e);
      load();
    }
  };

  const markAllRead = async () => {
    if (unreadCount === 0) return;
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
    try {
      await api.post("/notifications/mark-all-read");
    } catch (e) {
      toast.error(formatApiError(e, "Failed to mark all read"));
      load();
    }
  };

  const openMatch = (n) => {
    markOneRead(n.id);
    setOpen(false);
    if (n.related_match_id) {
      navigate(`/dashboard/matches?highlight=${n.related_match_id}`);
    } else {
      navigate("/dashboard/matches");
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openPanel}
        className="mb-focus-ring relative flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white transition-shadow hover:shadow-sm"
        aria-label="Notifications"
        data-testid="notification-bell-trigger"
      >
        <Bell className="h-4 w-4 text-gray-700" />
        {unreadCount > 0 && (
          <span
            className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#35622F] px-1 text-[10px] font-bold text-white ring-2 ring-white"
            data-testid="notification-unread-badge"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed z-[80] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_16px_48px_rgba(0,0,0,0.12)]"
            style={{
              top: `${pos.top}px`,
              right: `${pos.right}px`,
              width: `${pos.width}px`,
              maxHeight: "calc(100vh - 96px)",
            }}
            data-testid="notification-panel"
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  Notifications
                </div>
                <div
                  className="text-xs text-gray-500"
                  data-testid="notification-panel-count"
                >
                  {unreadCount > 0
                    ? `${unreadCount} unread`
                    : "You're all caught up"}
                </div>
              </div>
              <button
                type="button"
                onClick={markAllRead}
                disabled={unreadCount === 0}
                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-[#1B2B4B] transition hover:bg-[#1B2B4B]/10 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-transparent"
                data-testid="notification-mark-all-read"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all read
              </button>
            </div>

            <div
              className="overflow-y-auto"
              style={{ maxHeight: "min(60vh, 420px)" }}
            >
              {loading && items.length === 0 ? (
                <div className="px-5 py-10 text-center text-sm text-gray-400">
                  Loading…
                </div>
              ) : items.length === 0 ? (
                <div className="px-5 py-12 text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-50">
                    <Sparkles className="h-5 w-5 text-gray-400" />
                  </div>
                  <p className="mt-4 text-sm font-medium text-gray-700">
                    No notifications yet.
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    You&apos;ll see match alerts and network activity here.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {items.map((n) => (
                    <li key={n.id} data-testid={`notification-item-${n.id}`}>
                      <button
                        type="button"
                        onClick={() => openMatch(n)}
                        className={`flex w-full items-start gap-3 px-5 py-4 text-left transition hover:bg-gray-50 ${
                          n.is_read ? "" : "bg-[#1B2B4B]/[0.03]"
                        }`}
                      >
                        <span
                          className={`mt-1 h-2 w-2 flex-none rounded-full ${
                            n.is_read ? "bg-transparent" : "bg-[#35622F]"
                          }`}
                          aria-hidden
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-gray-900">
                            {n.title}
                          </div>
                          <div className="mt-0.5 line-clamp-2 text-xs text-gray-600">
                            {n.body}
                          </div>
                          <div className="mt-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                            {relativeTime(n.created_at)}
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
